import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { apiService } from "../services/api";
import { toast } from "sonner";
import LoadingSpinner from "../components/LoadingSpinner";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";

// Match Android: RESEND_TIMER_SECONDS = 60
const RESEND_TIMER_SECONDS = 60;

interface OTPVerificationPageProps {
  phoneNumber: string;
}

const OTPVerificationPage: React.FC<OTPVerificationPageProps> = ({
  phoneNumber,
}) => {
  const navigate = useNavigate();
  const { verifyOTP } = useAuth();
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [isLoading, setIsLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(RESEND_TIMER_SECONDS);
  const [canResend, setCanResend] = useState(false);

  // Countdown timer for resend OTP
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => {
        setResendTimer(resendTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [resendTimer]);

  // Auto-submit when all 6 digits are entered (matches Android behaviour)
  const handleVerify = useCallback(async (otpString: string) => {
    if (otpString.length !== 6) {
      toast.error("Please enter complete OTP");
      return;
    }

    setIsLoading(true);
    try {
      const result: { success: boolean; message: string; user?: any } =
        await verifyOTP(phoneNumber, otpString);
      if (result.success) {
        toast.success(result.message);

        if (result.user) {
          if (!result.user.google_signin_completed) {
            navigate("/email-verification");
          } else if (!result.user.empanelment_completed) {
            navigate("/pan-details");
          } else {
            navigate("/");
          }
        } else {
          navigate("/email-verification");
        }
      } else {
        toast.error(result.message);
        setOtp(["", "", "", "", "", ""]);
        setTimeout(() => document.getElementById("otp-0")?.focus(), 50);
      }
    } catch (error) {
      toast.error("Invalid OTP");
      setOtp(["", "", "", "", "", ""]);
      setTimeout(() => document.getElementById("otp-0")?.focus(), 50);
    } finally {
      setIsLoading(false);
    }
  }, [phoneNumber, verifyOTP, navigate]);

  const handleOtpChange = (index: number, value: string) => {
    // Multi-char value means browser pasted before onPaste could prevent default
    if (value.length > 1) {
      const digits = value.replace(/\D/g, "").slice(0, 6 - index);
      if (!digits) return;
      const newOtp = [...otp];
      for (let i = 0; i < digits.length; i++) {
        newOtp[index + i] = digits[i];
      }
      setOtp(newOtp);
      const lastFilled = Math.min(index + digits.length - 1, 5);
      document.getElementById(`otp-${lastFilled}`)?.focus();
      const complete = newOtp.join("");
      if (complete.length === 6 && !newOtp.includes("")) {
        handleVerify(complete);
      }
      return;
    }

    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (value && index < 5) {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        nextInput?.focus();
      }

      if (index === 5 && value) {
        const complete = [...newOtp.slice(0, 5), value].join("");
        if (complete.length === 6) {
          handleVerify(complete);
        }
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handlePaste = (index: number, e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6 - index);
    if (!pasted) return;
    const newOtp = [...otp];
    for (let i = 0; i < pasted.length; i++) {
      newOtp[index + i] = pasted[i];
    }
    setOtp(newOtp);
    const lastFilledIndex = Math.min(index + pasted.length - 1, 5);
    document.getElementById(`otp-${lastFilledIndex}`)?.focus();
    const complete = newOtp.join("");
    if (complete.length === 6 && !newOtp.includes("")) {
      handleVerify(complete);
    }
  };

  // Use dedicated /auth/resend-otp/ endpoint (matches Android)
  const handleResendOTP = async () => {
    if (!canResend) return;

    try {
      const response = await apiService.post("/auth/resend-otp/", {
        phone_number: phoneNumber,
        device_type: "web",
      });
      const data = response.data;
      if (data?.success) {
        toast.success("A new OTP has been sent to your number");
        setResendTimer(RESEND_TIMER_SECONDS);
        setCanResend(false);
        setOtp(["", "", "", "", "", ""]);
        document.getElementById("otp-0")?.focus();
      } else {
        const errorMessage = data?.message || "";
        if (errorMessage.toLowerCase().includes("wait")) {
          toast.error("Please wait before requesting another OTP");
          setResendTimer(RESEND_TIMER_SECONDS);
          setCanResend(false);
        } else {
          toast.error(errorMessage || "Failed to resend OTP. Please try again.");
        }
      }
    } catch (error) {
      toast.error("Failed to resend OTP. Please try again.");
    }
  };

  const formatPhoneNumber = (phone: string) => {
    if (phone.length === 10) {
      return `+91 ${phone.slice(0, 2)}******${phone.slice(-2)}`;
    }
    return phone;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col max-w-md mx-auto">
      {/* Header */}
      <div className="flex items-center px-6 py-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 -ml-2 rounded-full hover:bg-gray-100"
        >
          <ArrowLeftIcon className="h-6 w-6 text-gray-700" />
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1 px-6 py-8">
        <div className="max-w-sm mx-auto">
          {/* Title */}
          <h1 className="text-2xl font-semibold text-gray-900 mb-4">
            OTP Verification
          </h1>

          {/* Subtitle */}
          <p className="text-gray-500 text-sm mb-12 leading-relaxed">
            Enter the verification code we just sent to your number{" "}
            {formatPhoneNumber(phoneNumber)}
          </p>

          {/* OTP Input Boxes */}
          <div className="flex justify-center space-x-3 mb-16">
            {otp.map((digit, index) => (
              <input
                key={index}
                id={`otp-${index}`}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete={index === 0 ? "one-time-code" : "off"}
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={(e) => handlePaste(index, e)}
                className="w-12 h-12 text-center text-xl font-medium border-2 border-primary-300 rounded-lg focus:border-primary-500 focus:outline-none"
                style={{
                  borderColor: digit ? "#059669" : "#A7F3D0",
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Section */}
      <div className="px-6 pb-8">
        <div className="max-w-sm mx-auto">
          {/* Loading indicator while verifying */}
          {isLoading && (
            <div className="flex justify-center mb-4">
              <LoadingSpinner size="sm" />
            </div>
          )}

          {/* Resend OTP */}
          <div className="text-center">
            {canResend ? (
              <button
                onClick={handleResendOTP}
                disabled={isLoading}
                className="text-primary-600 text-sm font-medium hover:text-primary-700 disabled:opacity-50"
              >
                Didn't receive code? Resend
              </button>
            ) : (
              <p className="text-gray-500 text-sm">
                Resend OTP in 00:{resendTimer.toString().padStart(2, "0")}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OTPVerificationPage;
